import React from "react";
import OrderModal from "./OrderModal";
import {format, parse} from "date-fns";
import { toast } from 'react-toastify';
import {connect} from "react-redux"
import CustomToggle from "./OrderAccordionToggle";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Search from "./Search";
import SortFilterPopover from "./SortFilterPopover";
import { Accordion, Card } from "react-bootstrap";
import {
  createDADownload,
  createOrderDownloadSDS,
  getWebDownload,
  createLabDownload,
  postLogEvent,
} from "./utils/api";

const c_DefaultDateRangeInMonths = 1;
const c_DefaultExportDateRangeInMonths = 3;
const c_DefaultDateRangeInMonthsWhenSearchStringSet = 12;
const c_limit = 20;
const c_exportLimit = 200;
const c_exportLimitWhenDateRangeSet = 1000;
const c_UpdateIntervalMilliseconds = 300000;


const mapStateToProps = (state) => {
  return {
    companyId: state.app.companyId,
  };
};

function formatDateTime(dateStr, timeStr) {
  if (!dateStr) return '';

  // If timeStr exists, combine them, otherwise just use the date
  const dateTimeString = timeStr
    ? `${dateStr} ${timeStr}`
    : dateStr;

  const dt = new Date(dateTimeString);
  if (isNaN(dt)) return ''; // guard against invalid date

  const dd = String(dt.getDate()).padStart(2, '0');
  const MM = String(dt.getMonth() + 1).padStart(2, '0'); // zero-based
  const yyyy = dt.getFullYear();

  if (!timeStr) {
    // Only date required
    return `${dd}/${MM}/${yyyy}`;
  }

  // We have a time, so format HH:mm a
  let hours = dt.getHours();
  const minutes = String(dt.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // convert 0→12 for 12-hour clock
  const hh = String(hours).padStart(2, '0');

  return `${dd}/${MM}/${yyyy} ${hh}:${minutes} ${ampm}`;
}


class Orders extends React.Component {
  constructor() {
    super();
    
    // Load saved filter state if exists
    const savedState = this.loadFilterState();
    
    //initial state
    this.state = {
      addModalShow: false,
      selectedOrder: "",
      width: window.innerWidth,
      limit: c_limit,
      offset: 0,
      defaultDateRangeInMonths: c_DefaultDateRangeInMonths,
      searchString: savedState?.searchString || "",
      sortBy: savedState?.sortBy || "Date Ordered",
      filterDateStart: savedState?.filterDateStart || null,
      filterDateEnd: savedState?.filterDateEnd || null,
      appliedDateStart: savedState?.filterDateStart || null,
      appliedDateEnd: savedState?.filterDateEnd || null,
      appliedSearchString: savedState?.searchString || "",
      appliedDefaultDateRangeInMonths: c_DefaultDateRangeInMonths,
      filterStatuses: savedState?.filterStatuses || {
        Requested: false,
        Accepted: false,
        Complete: false,
        Cancelled: false,
      },
      companyName: "",
      activeKey: null,
      isLoading: false,
    };

    //bind methods here...
    this.addModalClose = this.addModalClose.bind(this);
    this.selectOrderEdit = this.selectOrderEdit.bind(this);
    this.duplicateOrder = this.duplicateOrder.bind(this);
    this.downloadDA = this.downloadDA.bind(this);
    this.logEvent = this.logEvent.bind(this);
    this.downloadOrderSDS = this.downloadOrderSDS.bind(this);
    this.downloadLabReport = this.downloadLabReport.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleFilterClear = this.handleFilterClear.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleOrderClick = this.handleOrderClick.bind(this);
    this.saveFilterState = this.saveFilterState.bind(this);
    this.loadFilterState = this.loadFilterState.bind(this);
  }

  intervalID = 0;

  // Save filter state to localStorage
  saveFilterState = () => {
    const filterState = {
      searchString: this.state.searchString,
      sortBy: this.state.sortBy,
      filterDateStart: this.state.filterDateStart?.toISOString(),
      filterDateEnd: this.state.filterDateEnd?.toISOString(),
      filterStatuses: this.state.filterStatuses,
    };
    localStorage.setItem('ordersFilterState', JSON.stringify(filterState));
  };

  // Load filter state from localStorage
  loadFilterState = () => {
    const saved = localStorage.getItem('ordersFilterState');
    if (saved) {
      try {
        const filterState = JSON.parse(saved);
        return {
          searchString: filterState.searchString || "",
          sortBy: filterState.sortBy || "Date Ordered",
          filterDateStart: filterState.filterDateStart ? new Date(filterState.filterDateStart) : null,
          filterDateEnd: filterState.filterDateEnd ? new Date(filterState.filterDateEnd) : null,
          filterStatuses: filterState.filterStatuses || {
            Requested: false,
            Accepted: false,
            Complete: false,
            Cancelled: false,
          },
        };
      } catch (e) {
        console.error("Error loading filter state:", e);
        return null;
      }
    }
    return null;
  };

  componentDidMount() {
    if (this.props.companyId) {
      this.props.clearOrders();
      this.getOrders();
    }
    window.addEventListener("resize", this.handleWindowSizeChange);
    this.intervalID = setInterval(() => {
      this.getOrders();
    }, c_UpdateIntervalMilliseconds);
  }

  componentDidUpdate(prevProps) {
    if (this.props.companyId !== prevProps.companyId) {
      this.setState({
        offset: 0,
        defaultDateRangeInMonths: c_DefaultDateRangeInMonths,
      }, () => {
        // This is called after the state has actually updated
        this.getOrders();
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleWindowSizeChange);
    clearInterval(this.intervalID);
  }

  handleWindowSizeChange = () => {
    this.setState({ width: window.innerWidth });
  };

  exportFilteredOrders = async () => {
    let dateRange =
      this.state.filterDateStart &&
      this.state.filterDateEnd &&
      this.state.filterDateStart <= this.state.filterDateEnd
        ? format(this.state.filterDateStart, "MM/dd/yyyy") +
          "..." +
          format(this.state.filterDateEnd, "MM/dd/yyyy")
        : "";
    const noDateRange = !dateRange;
    let dateField = this.state.sortBy;
    const ordersCount = this.props.orders?.length || 0;
    let limit = c_exportLimitWhenDateRangeSet;
  
    if (noDateRange) {
      const today = new Date();
      const defaultDateRange = [
        new Date(today.getFullYear(), today.getMonth() - Math.max(this.state.defaultDateRangeInMonths, c_DefaultExportDateRangeInMonths), today.getDate()),
        today
      ]
        .map(d => [
          String(d.getMonth() + 1).padStart(2, '0'),
          String(d.getDate()).padStart(2, '0'),
          d.getFullYear()
        ].join('/'))
        .join('...');
        dateRange = defaultDateRange;
        dateField = "Date Ordered";
        limit = c_exportLimit;
    }
    
    if (ordersCount > limit) {
      limit = ordersCount;
    }
    
    await this.props.exportOrders(
      this.state.searchString,
      undefined,
      limit,
      this.state.filterStatuses,
      dateRange,
      dateField,
      this.state.sortBy,
      this.props.companyId
    );
  };

  getOrders = async () => {
    this.setState({ isLoading: true });
    let dateRange =
      this.state.filterDateStart &&
      this.state.filterDateEnd &&
      this.state.filterDateStart <= this.state.filterDateEnd
        ? format(this.state.filterDateStart, "MM/dd/yyyy") +
          "..." +
          format(this.state.filterDateEnd, "MM/dd/yyyy")
        : "";
    const noDateRange = !dateRange;
    let dateField = this.state.sortBy;
    let defaultDateRangeInMonths = this.state.defaultDateRangeInMonths;
    if (this.state.searchString && defaultDateRangeInMonths < c_DefaultDateRangeInMonthsWhenSearchStringSet) {
      defaultDateRangeInMonths = c_DefaultDateRangeInMonthsWhenSearchStringSet;
    }
    if (noDateRange) {
      const today = new Date();
      const defaultDateRange = [
        new Date(today.getFullYear(), today.getMonth() - defaultDateRangeInMonths, today.getDate()),
        today
      ]
        .map(d => [
          String(d.getMonth() + 1).padStart(2, '0'),
          String(d.getDate()).padStart(2, '0'),
          d.getFullYear()
        ].join('/'))
        .join('...');
        dateRange = defaultDateRange;
        dateField = "Date Ordered";
    }
    try {
      const ordersCount = this.props.orders?.length || 0;
      await this.props.getOrdersNew(
        ordersCount < this.state.limit ? this.state.limit : ordersCount,
        this.state.searchString,
        0,
        this.state.filterStatuses,
        dateRange,
        dateField,
        this.state.sortBy,
        false
      );
      const newOffset = this.props.orders.length + 1;
      this.setState({
        offset: newOffset,
        defaultDateRangeInMonths: defaultDateRangeInMonths,
        appliedDateStart: this.state.filterDateStart,
        appliedDateEnd: this.state.filterDateEnd,
        appliedSearchString: this.state.searchString,
        appliedDefaultDateRangeInMonths: noDateRange ? defaultDateRangeInMonths : null,
      });
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      this.setState(
      { 
        isLoading: false 
      });
    }
  };

  getMoreOrders = async () => {
    if (this.state.isLoading) return;
    this.setState({ isLoading: true });
    const ordersCount = this.props.orders?.length || 0;
    const offset = ordersCount + 1;
    let dateRange =
      this.state.filterDateStart &&
      this.state.filterDateEnd &&
      this.state.filterDateStart <= this.state.filterDateEnd
        ? format(this.state.filterDateStart, "MM/dd/yyyy") +
          "..." +
          format(this.state.filterDateEnd, "MM/dd/yyyy")
        : "";
    const noDateRange = !dateRange;
    let dateField = this.state.sortBy;
    let defaultDateRangeInMonths = this.state.defaultDateRangeInMonths;
    if (this.state.searchString && defaultDateRangeInMonths < c_DefaultDateRangeInMonthsWhenSearchStringSet) {
      defaultDateRangeInMonths = c_DefaultDateRangeInMonthsWhenSearchStringSet;
    }
    if (noDateRange) {
      const today = new Date();
      if (ordersCount < this.state.limit) {
        defaultDateRangeInMonths = defaultDateRangeInMonths + 1;
      }
      const defaultDateRange = [
        new Date(today.getFullYear(), today.getMonth() - defaultDateRangeInMonths, today.getDate()),
        today
      ]
        .map(d => [
          String(d.getMonth() + 1).padStart(2, '0'),
          String(d.getDate()).padStart(2, '0'),
          d.getFullYear()
        ].join('/'))
        .join('...');
        dateRange = defaultDateRange;
        dateField = "Date Ordered";
    }
    try {
      await this.props.getOrdersNew(
        this.state.limit,
        this.state.searchString,
        offset,
        this.state.filterStatuses,
        dateRange,
        dateField,
        this.state.sortBy,
        true
      );
      const newOrdersCount = this.props.orders?.length || 0;
      const newOffset = newOrdersCount + 1;
      if (noDateRange && (newOrdersCount - ordersCount < this.state.limit)) 
      {
        defaultDateRangeInMonths = defaultDateRangeInMonths + 1;
      }
      this.setState({
        offset: newOffset,
        defaultDateRangeInMonths: defaultDateRangeInMonths,
        appliedDefaultDateRangeInMonths: noDateRange ? defaultDateRangeInMonths : null,
      });
    } catch (error) {
      console.error("Failed to fetch orders, running getMoreOrders:", error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleSearchChange(value) {
    this.setState({
      searchString: value,
    }, () => {
      this.saveFilterState();
    });
  }

  handleSearch = async (state) => {
    let limit = c_limit; //returns list of results back to 20
    await this.props.clearOrders();
    this.setState({
      limit: limit,
      defaultDateRangeInMonths: c_DefaultDateRangeInMonths,
      offset: 0,
    }, async () => {
      await this.getOrders();
    });
  };

  handleFilterChange(name, value) {
    this.setState({
      filterStatuses: {
        ...this.state.filterStatuses,
        [name]: value,
      },
    }, () => {
      this.saveFilterState();
    });
  }

  handleFilterClear() {
    this.setState(
      {
        ...this.state,
        sortBy: "Date Ordered",
        filterDateStart: null,
        filterDateEnd: null,
        filterStatuses: {
          Requested: false,
          Accepted: false,
          Completed: false,
          Cancelled: false,
        },
      },
      () => {
        this.saveFilterState();
        this.handleSearch();
      }
    );
  }

  handleInputChange(name, value) {
    if (name === 'filterDateStart' && value && !this.state.filterDateEnd) {
      this.setState({
        filterDateEnd: new Date(),
        [name]: value,
      }, () => {
        this.saveFilterState();
      });
    } else {
      this.setState({
        [name]: value,
      }, () => {
        this.saveFilterState();
      });
    }
  }

  selectOrderEdit(order) {
    this.setState({
      selectedOrder: order,
      addModalShow: true,
      addModalMode: "edit",
    });
  }

  newOrder() {
    this.setState({
      addModalShow: true,
      addModalMode: "new",
      recordId: null,
    });
  }

  duplicateOrder(order) {
    this.setState({
      selectedOrder: order,
      addModalShow: true,
      addModalMode: "duplicate",
    });
  }

  selectOrderCancel(order) {
    this.setState({
      selectedOrder: order,
      addModalShow: true,
      addModalMode: "cancel",
    });
  }

  handleOrderClick = (key) => {
    this.setState((prevState) => ({
      activeKey: prevState.activeKey === key ? null : key,
    }));
  };
   
  // Generic download handler
  handleDownload = async ({
    recordId,
    createDownloadFn,
    downloadType,
    itemName
  }) => {
    const toastId = toast.info(
      `⏱ ${downloadType === 'Docket' || downloadType === 'Lab Report' ? 'Generating' : 'Downloading'} ${downloadType} for ${itemName}...`,
      {
        autoClose: false,
        pauseOnFocusLoss: false,
      }
    );
  
    try {
      // Create download record if needed
      if (!recordId) {
        recordId = await createDownloadFn();
      }
      
      // Check for error message
      const message = recordId?.message;
      if (message) {
        toast.update(toastId, {
          render: message,
          type: "error",
          autoClose: 3000,
        });
        return;
      }
      
      // Update toast while preparing download
      toast.update(toastId, {
        render: `Opening ${downloadType}...`,
        type: "success",
        autoClose: false,
      });
      
      // Get the file URL
      const fileUrl = await getWebDownload(recordId, "");
      const newWindow = window.open(fileUrl, "_blank");
      setTimeout(async () => {
        toast.dismiss(toastId);
      }, 250);
      
    } catch (error) {
      console.error(`${downloadType} download error:`, error);
      toast.update(toastId, {
        render: error.message || "Download failed!",
        type: "error",
        autoClose: 3000,
      });
    }
  };
  
  downloadDA = async (order) => {
    const deliveryId = order.fieldData["Order_DELIVERY_delete::__pk_Record_UUID"];
    const recordId = order.fieldData["Order_Delivery_delete_WEBDOWNLOADS::record_id"];
    const docketNumber = order.fieldData["Order_DELIVERY_delete::SAMI_Docket_Number"];
    
    await this.handleDownload({
      recordId,
      createDownloadFn: () => createDADownload(deliveryId),
      downloadType: "Docket",
      itemName: `DA#${docketNumber}`
    });
  };
  
  logEvent = async (order, eventName) => {
    const orderNum = order.fieldData["SBT_Order_Number"];
    postLogEvent(orderNum, eventName);
  };
  
  downloadOrderSDS = async (order) => {
    const orderId = order.fieldData["__pk_Record_UUID"];
    const recordId = order.fieldData["Order_ORDERLINE_delete_WEBDOWNLOADS_SDS::record_id"];
    const productName = order.fieldData["Order_OrderLine_PRODUCT::Name"];
    
    await this.handleDownload({
      recordId,
      createDownloadFn: () => createOrderDownloadSDS(orderId),
      downloadType: "SDS",
      itemName: productName
    });
  };
  
  downloadLabReport = async (order) => {
    const deliveryId = order.fieldData["Order_DELIVERY_delete::__pk_Record_UUID"];
    const recordId = order.fieldData["Order_Delivery_delete_WEBDOWNLOADS_TestReport::record_id"];
    const docketNumber = order.fieldData["Order_DELIVERY_delete::SAMI_Docket_Number"];
    
    await this.handleDownload({
      recordId,
      createDownloadFn: () => createLabDownload(deliveryId),
      downloadType: "Lab Report",
      itemName: `DA#${docketNumber}`
    });
  };

  renderLoadingSkeleton = (index, width) => {
    return (
      <div key={index} className="">
        <SkeletonTheme baseColor="#1A5D6D" highlightColor="#376D7C">
          <div className="accordion-button">
            {/* order number skeleton */}
            <div className="left">
              <Skeleton width={100} />
            </div>
            {/* orderDate skeleton */}
            {width > 768 ? (
              <div>
                <Skeleton width={100} />
              </div>
            ) : (
              ""
            )}
            {/* product name skeleton */}
            <div>
              <Skeleton width={200} />
            </div>
            {/* method and delivery date skeleton */}
            {width >= 576 ? (
              <div>
                <Skeleton width={85} />
              </div>
            ) : (
              ""
            )}
            {/* status skeleton */}
            <div className="right">
              <Skeleton width={85} />
            </div>
          </div>
        </SkeletonTheme>
      </div>
    );
  };

  renderOrder = (key) => {
    const order = this.props.orders[key];
    try {
      if (!order) {
        throw new Error("Error rendering order: Order is undefined");
      }
      const SBTOrderNumber = order.fieldData?.SBT_Order_Number;
      const orderLine = order.portalData.Order_ORDERLINE_web_create[0];
      const orderDate = () => {
        const parts = order.fieldData.Date_Order.split("/");
        const newDate = new Date(parts[2], parts[0] - 1, parts[1]);
        return format(newDate, "dd/MM/yyyy");
      };
      const fulfilmentTimestamp = () => {
        const method =
          order.fieldData["Order_CARRIERJOIN_depot_load::Carrier_Organised_By"];

        const timestamp =
          method === "SAMI"
            ? order.fieldData[
                "Order_CARRIERJOIN_depot_load::DeliveryTimeStamp_c"
              ]
            : order.fieldData["Order_CARRIERJOIN_depot_load::Load_TimeStamp_c"];
        if (timestamp === "") {
          return "";
        } else {
          const date = new Date(timestamp);
          if (Number.isNaN(date.getTime())) return "";
          const pad = (n, digits = 2) => String(n).padStart(digits, "0");
          const dd = pad(date.getDate());
          const MM = pad(date.getMonth() + 1);
          const yy = String(date.getFullYear()).slice(-2);
          let hours24 = date.getHours();
          const nbsp = "\u00A0";
          const ampm = hours24 >= 12 ? "PM" : "AM";
          const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
          const HH = pad(hours12);
          const mm = pad(date.getMinutes());
          return `${dd}/${MM}/${yy} ${HH}:${mm}${nbsp}${ampm}`;
        }
      };
      const fulfilmentMethod = () => {
        const method =
          order.fieldData["Order_CARRIERJOIN_depot_load::Carrier_Organised_By"];
        if (!method) {
          return "";
        } else if (method === "SAMI") {
          return "Delivery";
        } else if (method === "Client") {
          return "Pick-up";
        }
      };
      const status = order.fieldData.Status_c ;

      // Make sure the order is loaded before we continue!
      if (!order) return null;

      const { width } = this.state;
      // const isMobile = width <= 768;

      return (
        <Accordion.Item key={key} eventKey={SBTOrderNumber || key}>
          <Accordion.Header>
            <div className="left">
              {order.fieldData.SBT_Order_Number}
              <br />
              {order.fieldData.Order_Number_Customer}
            </div>
            {width > 768 ? <div>{orderDate()}</div> : ""}
            <div>
              {orderLine &&
                orderLine["Order_ORDERLINE_web_create::Qty_Ordered"] &&
                orderLine["Order_ORDERLINE_web_create::Qty_Ordered"]
                  .toLocaleString("en", { useGrouping: true }) +
                " " +
                (
                  orderLine["Order_ORDERLINE_web_create::Unit_of_Measure_Short_ordered_ae"] === "Units" &&
                  orderLine["Order_ORDERLINE_web_create::Qty_Ordered"] === 1
                    ? "Unit"
                    : orderLine["Order_ORDERLINE_web_create::Unit_of_Measure_Short_ordered_ae"]
                        ?.replace(/kg/gi, "kg")
                        .replace(/l/g, "L")
                ) +
                " " +
                orderLine["Order_ORDERLINE_web_create::ItemName_inc_Variant_c"]
              }
            
              {order.fieldData["Order_Delivery_DELIVERYLINE::Final_Weight_Total_Load_c"] ? (
                <>
                  <br />
                  {(() => {
                    const weight = order.fieldData["Order_Delivery_DELIVERYLINE::Final_Weight_Total_Load_c"];
                    const volume = order.fieldData["Order_Delivery_DELIVERYLINE::Final_Volume_Product_andCutter_c"];
                    const isNegative = weight < 0;
                    const absWeight = Math.abs(weight).toLocaleString("en", { useGrouping: true });
                    const absVolume = Math.abs(volume).toLocaleString("en", { useGrouping: true });
                    const textVolume = volume ? `/ ${absVolume} L` : null;
                    const style = { color: isNegative ? "red" : "inherit" };
                    return (
                      <span style={style}>
                        {absWeight} kg {textVolume} {isNegative ? "returned" : "loaded"}
                      </span>
                    );
                  })()}
                </>
              ) : null}
            </div>
            {width >= 576 ? (
              <div>
                {fulfilmentMethod()}
                <br />
                {fulfilmentTimestamp()}
              </div>
            ) : (
              ""
            )}
            <div className="right">
              <span className={"status " + status.toLowerCase()}>{status}</span>
            </div>
          </Accordion.Header>
          <Accordion.Body eventKey={key}>
            <Card.Body>
              <div className="card-column">
                <div className="order-label">SBT No:</div>
                <div className="order-data">
                  {order.fieldData.SBT_Order_Number}
                </div>

                <div className="order-label">Your Order No:</div>
                <div className="order-data">
                  {order.fieldData.Order_Number_Customer}
                </div>

                <div className="order-label">Order Date:</div>
                <div className="order-data">{orderDate()}</div>

                <div className="order-label">Product:</div>
                <div className="order-data">
                  {orderLine &&
                    orderLine[
                      "Order_ORDERLINE_web_create::ItemName_inc_Variant_c"
                    ] &&
                    orderLine[
                      "Order_ORDERLINE_web_create::ItemName_inc_Variant_c"
                    ]}
                </div>

                <div className="order-label">Order Qty:</div>
                <div className="order-data">
                  {
                    orderLine &&
                    orderLine["Order_ORDERLINE_web_create::Qty_Ordered"] &&
                    orderLine[
                        "Order_ORDERLINE_web_create::Qty_Ordered"
                      ].toLocaleString("en", { useGrouping: true }) +
                    " " +
                    (
                      orderLine["Order_ORDERLINE_web_create::Unit_of_Measure_Short_ordered_ae"] === "Units" &&
                      orderLine["Order_ORDERLINE_web_create::Qty_Ordered"] === 1
                        ? "Unit"
                        : orderLine["Order_ORDERLINE_web_create::Unit_of_Measure_Short_ordered_ae"]
                            ?.replace(/kg/gi, "kg")
                            ?.replace(/l/g, "L") || ""
                    )
                  }
                </div>
                {order.fieldData["Order_Delivery_DELIVERYLINE::Final_Weight_Total_Load_c"] ? (
                <>
                  <div className="order-label">Final weight:</div>
                  <div className="order-data">
                    {order &&
                      order.fieldData[
                        "Order_Delivery_DELIVERYLINE::Final_Weight_Total_Load_c"
                      ].toLocaleString("en", { useGrouping: true }) + " kg"
                    }
                  </div>
                </>
                  ) : null
                }
              </div>
              <div className="card-column">
                <div className="order-label">From Depot:</div>
                <div className="order-data">
                  {order.fieldData["Order_LOCATION::Name"]}
                </div>

                <div className="order-label">Despatch Method:</div>
                <div className="order-data">{fulfilmentMethod()}</div>

                {fulfilmentMethod() === "Delivery" ? (
                  <>
                    <div className="order-label">Delivery Point:</div>
                    <div className="order-data">
                      {
                        order.fieldData[
                          "Order_CARRIERJOIN_depot_load::Delivery_Point"
                        ]
                      }
                    </div>
                    <div className="order-label">Delivery:</div>
                    <div className="order-data">
                      {formatDateTime(
                        order.fieldData["Order_CARRIERJOIN_depot_load::Delivery_Date"],
                        order.fieldData["Order_CARRIERJOIN_depot_load::Delivery_Time"]
                      )}
                    </div>
                  </>
                ) : (
                  ""
                )}
                {order.fieldData["Order_CARRIERJOIN_depot_load::Load_Date"] ? (
                  <>
                    <div className="order-label">Load:</div>
                    <div className="order-data">
                      {formatDateTime(
                        order.fieldData["Order_CARRIERJOIN_depot_load::Load_Date"],
                        order.fieldData["Order_CARRIERJOIN_depot_load::Load_Time"]
                      )}
                    </div>
                  </> ) : null
                }
                {order.fieldData["Requested_Account_Name"] ? (
                  <>
                    <div className="order-label">Ordered By:</div>
                    <div className="order-data">
                      {order.fieldData["Requested_Account_Name"]}
                    </div>
                  </> ) : null
                }
              </div>
              {order.fieldData["Flag_Current_Order_Web_c"] === 1 ? (
                <>
                  <div className="card-column-actions">
                    {orderLine &&
                      orderLine[
                        "Order_ORDERLINE_web_create::ItemName_inc_Variant_c"
                      ] && (
                        <button
                          className={
                            this.props.viewOnly
                              ? "view-only-btn"
                              : "edit-btn"
                          }
                          onClick={(e) => {
                            this.props.viewOnly
                              ? alert(
                                  "Permission Denied, this is a View-Only user account"
                                )
                              : this.duplicateOrder(order);
                          }}
                        >
                          <img
                            className="file-icon"
                            height="30"
                            src="/images/duplicate.png"
                            alt="Duplicate"
                          />{" "}
                          Duplicate
                        </button>
                      )}
                    <button
                      className={
                        this.props.viewOnly
                          ? "view-only-btn"
                          : "edit-btn"
                      }
                      onClick={(e) => {
                        this.props.viewOnly
                          ? alert(
                              "Permission Denied, this is a View-Only user account"
                            )
                          : this.selectOrderEdit(order);
                      }}
                    >
                      <img
                        className="file-icon"
                        height="30"
                        src="/images/edit.png"
                        alt="Edit"
                      />{" "}
                      Edit Request
                    </button>
                    <button
                      className={
                        this.props.viewOnly
                          ? "view-only-btn"
                          : "edit-btn cancel-btn"
                      }
                      onClick={(e) => {
                        this.props.viewOnly
                          ? alert(
                              "Permission Denied, this is a View-Only user account"
                            )
                          : this.selectOrderCancel(order);
                      }}
                    >
                      <img
                        className="file-icon"
                        height="30"
                        src="/images/cancel.png"
                        alt="Cancel"
                      />{" "}
                      Cancel Order
                    </button>
                    {order.fieldData["Order_OrderLine_PRODUCT::Has_SDS_c"] == 1 ? 
                      (
                      <button
                        className="edit-btn"
                        onClick={(e) => {this.downloadOrderSDS(order)}}
                        >
                        <img
                            className="file-icon"
                            height="30"
                            src="/images/pdf.svg"
                            alt="PDF"
                          />{" "}
                        SDS
                      </button>
                      ): null
                  }
                  </div>
                </>
              ) : (
                <>
                <div className="card-column-actions">
                  {orderLine &&
                    orderLine[
                      "Order_ORDERLINE_web_create::ItemName_inc_Variant_c"
                    ] && (
                      <button
                        className={
                          this.props.viewOnly
                            ? "view-only-btn"
                            : "edit-btn"
                        }
                        onClick={(e) => {
                          this.props.viewOnly
                            ? alert(
                                "Permission Denied, this is a View-Only user account"
                              )
                            : this.duplicateOrder(order);
                        }}
                      >
                        <img
                          className="file-icon"
                          height="30"
                          src="/images/duplicate.png"
                          alt="Duplicate"
                        />{" "}
                        Duplicate
                      </button>
                    )}
                    {order.fieldData["Order_DELIVERY_delete::SAMI_Docket_Number"] &&
                     order.fieldData["Order_DELIVERY_delete::Flag_Complete"] &&
                      (
                        <button
                          className="edit-btn"
                          onClick={() => this.downloadDA(order)}
                        >
                          <img
                            className="file-icon"
                            height="30"
                            src="/images/pdf.svg"
                            alt="PDF"
                          />{" "}
                          {"DA " +
                            order.fieldData[
                              "Order_DELIVERY_delete::SAMI_Docket_Number"
                            ]}
                        </button>
                      )
                    }
                    {order.fieldData[
                      "Order_DELIVERY_delete::Flag_Has_Test_Report_c"
                    ] && (
                      <button
                        className="edit-btn"
                        onClick={() => this.downloadLabReport(order)}
                      >
                        <img
                          className="file-icon"
                          height="30"
                          src="/images/pdf.svg"
                          alt="PDF"
                        />{" "}
                        Lab Report
                      </button>
                    )}
                    {order.fieldData["Order_OrderLine_PRODUCT::Has_SDS_c"] == 1 ? 
                      (
                      <button
                        className="edit-btn"
                        onClick={(e) => {this.downloadOrderSDS(order)}}
                        >
                        <img
                            className="file-icon"
                            height="30"
                            src="/images/pdf.svg"
                            alt="PDF"
                          />{" "}
                        SDS
                      </button>
                      ): null
                    }
                  </div>
                </>
              )}
            </Card.Body>
          </Accordion.Body>
        </Accordion.Item>
      );
    } catch (error) {
      console.log("Error rendering order: ", error);
      const orderNumber = order?.fieldData?.SBT_Order_Number || "Unknown";
      return (
        <div className="order-row-error" key={key}>
          <p>
            Error rendering order #{orderNumber}:<pre>{error.message}</pre>
          </p>
        </div>
      );
    }
  };

  addModalClose() {
    this.getOrders();
    this.setState({
      addModalShow: false,
    });
  }

  getTitleWithDateRange = () => {
    const baseTitle = this.state.appliedSearchString ? "Search Results" : "Recent Orders";
    if (this.state.appliedDateStart && this.state.appliedDateEnd) {
      const startFormatted = format(this.state.appliedDateStart, "dd/MM/yyyy");
      const endFormatted = format(this.state.appliedDateEnd, "dd/MM/yyyy");
      return `${baseTitle} (${startFormatted} - ${endFormatted})`;
    } else if (this.state.appliedDefaultDateRangeInMonths) {
      const months = this.state.appliedDefaultDateRangeInMonths;
      const monthLabel = months === 1 ? "month" : "months";
      return `${baseTitle} (${months} ${monthLabel})`;
    } else {
      return baseTitle;
    }
  };

  render() {
    const { width, isLoading } = this.state;
    const orderIds = Object.keys(this.props.orders);
    let title = "";
    if (this.props.companyId) {
      title = this.getTitleWithDateRange();
    } else {
      title = "Loading... ";
    }
    return (
      <div className="orders-container">
        <h2 className="heading">{title}</h2>
        {this.props.companyId && ( 
        <div>
          <Search
            searchString={this.state.searchString}
            onSearchChange={this.handleSearchChange}
            handleSearch={this.handleSearch}
          />
        </div>
        )}

        {this.props.companyId && ( 
        <div>  
          <SortFilterPopover
            filterStatuses={this.state.filterStatuses}
            onFilterChange={this.handleFilterChange}
            handleSearch={this.handleSearch}
            handleFilterClear={this.handleFilterClear}
            handleInputChange={this.handleInputChange}
            dateFilterStart={this.state.filterDateStart}
            dateFilterEnd={this.state.filterDateEnd}
            sortBy={this.state.sortBy}
            width={width}
          />
        </div>
        )}

        {this.props.companyId && ( 
        <div className="button-container">
          <button
            className={
              this.props.viewOnly ? "view-only-new-btn" : "new-btn"
            }
            onClick={(e) => {
              this.props.viewOnly
                ? alert("Permission Denied, this is a View-Only user account")
                : this.newOrder();
            }}
          >
            <i className="fas fa-plus"></i> New Order
          </button>
        </div>
        )}

        <div className="orders-header">
          <div className="order-label">
            SBT No
            <br />
            Ref #
          </div>
          {width > 768 ? <div className="order-label">Order Date</div> : ""}
          <div className="order-label">Product</div>
          {width >= 576 ? (
            <div className="order-label">Despatch Method & Date</div>
          ) : (
            ""
          )}
          <div className="order-label right">Status</div>
        </div>

        {this.props.orders.messages &&
          this.props.orders.messages[0].code === "401" && (
            <div>No records found</div>
          )}

        {/* render the orders here */}
        {Array.isArray(this.props.orders) && this.props.orders.length > 0 && (
          <Accordion flush>{orderIds.map(this.renderOrder)}</Accordion>
        )}

        {isLoading && (
          <div className="accordion-loading-skelton-container">
            {this.renderLoadingSkeleton(1, width)}
            {this.renderLoadingSkeleton(2, width)}
            {this.renderLoadingSkeleton(3, width)}
            {this.renderLoadingSkeleton(4, width)}
            {this.renderLoadingSkeleton(5, width)}
          </div>
        )}
        {this.props.companyId && (
        <div className="more">
          <button className="more-btn" onClick={() => this.getMoreOrders()}>
            Load more
          </button>
          <button
            className={
              this.props.canExport ? "more-btn" : "more-btn-disabled"
            }
            onClick={(e) => {
              this.props.canExport
                ? this.exportFilteredOrders()
                : alert("Permission denied. This account does not have permission to export the list.");
            }}
          >
            Export List
          </button>
        </div>
        )}
        <OrderModal
          show={this.state.addModalShow}
          onHide={this.addModalClose}
          locations={this.props.locations}
          order={this.state.selectedOrder}
          mode={this.state.addModalMode}
          userFullName={this.props.userFullName}
          deliveryPointDefault={this.props.deliveryPointDefault}
          carrierPref={this.props.carrierPref}
          peopleOnsite={this.props.peopleOnsite}
        />
      </div>
    );
  }
}

export default connect(mapStateToProps)(Orders);